import { DtoForceConstructor, DtoImport, DtoExport } from '@core/utils/dto';
import { ListItem } from '../common/list-item';
import { Entity } from '@core/data/dto/entity';
import { OrderType } from './order-type';
import { DeliveryOrderStatus } from './delivery-order-status';
import { DeliveryOrderQuote } from './quote/delivery-order-quote';
import { Moment } from 'moment';
import { MapMarker } from '../map/map-marker';
import { Dto } from '@core/data/dto/dto';
import { DTO_CUSTOM, DTO_DEFAULT, DTO_DEFAULT_CREATE, DtoType } from '@core/data/dto/dto-types';
import { ListGroupBy } from '../common/list-group-by';
import { ListSortBy } from '../common/list-sort-by';
import { DeliveryOrderItem } from './item/delivery-order-item';
import { DeliveryOrderManagerNote } from './manager-note/delivery-order-manager-note';
import { DeliveryOrderDropoff } from './dropoff/delivery-order-dropoff';
import { DeliveryOrderPickup } from './pickup/delivery-order-pickup';
import { DeliveryOrderCourier } from './courier/delivery-order-courier';
import { DeliverableAction } from './deliverable-action';
import { DeliveryServiceProvider } from './delivery-service-provider';
import { DeliveryOrderSchema } from './delivery-order.schema';
import { DeliveryOrderDriver } from './driver/delivery-order-driver';
import { DeliveryOrderRefund } from './refund/delivery-order-refund';
import { DeliveryOrderWarning } from './delivery-order-warning';
import { DeliveryOrderEvent } from './delivery-order-event';
import { DeliveryOrderPaymentMethod } from './delivery-order-payment-method';
import { DeliveryOrderQuoteRequestSchema } from './delivery-order-quote-request.schema';
import { CreateDeliveryOrderRequestSchema } from './create-delivery-order-request.schema';

@DtoForceConstructor
export class DeliveryOrder implements ListItem, Entity {
    public id: string;
    public externalId: string;
    public externalCode: string;
    public orderReference: string;
    public reference: string;
    public orderType: OrderType;
    public orderId?: string;
    public status: DeliveryOrderStatus;
    public quote: DeliveryOrderQuote;
    public pickup: DeliveryOrderPickup;
    public dropoff: DeliveryOrderDropoff;
    public orderItems: DeliveryOrderItem[];
    public distance: number;
    public createdAt: Moment;
    public updatedAt: Moment;
    public pickupAtReady: Moment;
    public pickupAtEta: Moment;
    public pickupAt: Moment;
    public dropoffAtEta: Moment;
    public dropoffAt: Moment;
    public deliveryTimeUtc: Moment;
    public fullServiceProvider: string;
    public fee: number;
    public liveDropoffAtEta: Moment;
    public livePickupAtEta: Moment;
    public tip: number;
    public currency: string;
    public tax: number;
    public total: number;
    public trackingUrl: string;
    public courier: DeliveryOrderCourier;
    public requiresId: boolean;
    public requiresDropoffSignature: boolean;
    public deliverableAction: DeliverableAction;
    public location: {
        id: string;
    };
    public company: {
        id: string;
    };
    public serviceProvider: DeliveryServiceProvider;
    public provider?: string;
    public providers?: string[];
    public driverId?: string;
    public warnings: DeliveryOrderWarning[];
    public refund: DeliveryOrderRefund;
    public managerNote: DeliveryOrderManagerNote;

    public loading: boolean = false;
    public selected: boolean;
    public events: DeliveryOrderEvent[];
    public errorCode: string;
    public attributes: {
        refundable: boolean;
    };
    public driver: DeliveryOrderDriver;
    public paymentMethods: DeliveryOrderPaymentMethod[];
    public overrideForceAssign?: boolean;
    public markers?: MapMarker[];
    public pickupAtReadyDescription?: string;
    public pickupAtReadyDescriptionParams?: { date, time };
    public statusLabel?: string;

    public static readonly ACTIVE_ORDERS: DeliveryOrderStatus[] = [
        DeliveryOrderStatus.NEW,
        DeliveryOrderStatus.ACCEPTED,
        DeliveryOrderStatus.ASSIGNED,
        DeliveryOrderStatus.PICKUP,
        DeliveryOrderStatus.DROPOFF,
        DeliveryOrderStatus.REJECTED,
        DeliveryOrderStatus.RETURNING,
        DeliveryOrderStatus.SCHEDULED
    ];

    public static readonly LONG;

    /**
     * Uses order item quantity
     */
    public get orderTotalQuantity(): number {
        let totalQuantity: number = 0;
        if (this.orderItems?.length > 0) {
            totalQuantity = this.orderItems
                .map((orderItem: DeliveryOrderItem) => orderItem.quantity)
                .reduce(
                    (accumulator, currentValue) => accumulator + currentValue
                );
        }
        return totalQuantity;
    }

    /**
     * direct access to customer name
     */
    public get customerName() {
        return this.dropoff.name;
    }

    /**
     * direct access to customer phone
     */
    public get customerPhone() {
        return this.dropoff.phoneNumber;
    }

    /**
     * direct access to customer address
     */
    public get customerAddress() {
        return this.dropoff.address;
    }

    /**
     * direct access to customer address
     */
    public get deliveryInstruction() {
        return this.dropoff.notes;
    }

    @DtoImport(DeliveryOrderSchema)
    static import(dto: Dto): any {}

    constructor(value?: {
        id: string;
        externalId: string;
        externalCode: string;
        orderReference: string;
        reference: string;
        orderType: OrderType;
        orderId?: string;
        status: DeliveryOrderStatus;
        quote: DeliveryOrderQuote;
        pickup: DeliveryOrderPickup;
        dropoff: DeliveryOrderDropoff;
        orderItems: DeliveryOrderItem[];
        distance: number;
        createdAt: Moment;
        updatedAt: Moment;
        pickupAtReady: Moment;
        pickupAtEta: Moment;
        pickupAt: Moment;
        dropoffAtEta: Moment;
        dropoffAt: Moment;
        liveDropoffAtEta: Moment;
        livePickupAtEta: Moment;
        deliveryTimeUtc: Moment;
        fullServiceProvider: string;
        fee: number;
        tip: number;
        currency: string;
        tax: number;
        total: number;
        trackingUrl: string;
        courier: DeliveryOrderCourier;
        driver: DeliveryOrderDriver;
        requiresId: boolean;
        requiresDropoffSignature: boolean;
        deliverableAction: DeliverableAction;
        location: {
            id: string;
        };
        company: {
            id: string;
        };
        refund: DeliveryOrderRefund;
        serviceProvider: DeliveryServiceProvider;
        providers?: string[];
        driverId?: string;
        managerNote: DeliveryOrderManagerNote;
        warnings: DeliveryOrderWarning[];
        attributes: {
            refundable: boolean;
        };
        paymentMethods: DeliveryOrderPaymentMethod[];
        errorCode: string;
        loading: boolean;
        selected: boolean;
    }) {
        if (value) {
            this.id = value.id;
            this.externalId = value.externalId;
            this.externalCode = value.externalCode;
            this.orderReference = value.orderReference;
            this.reference = value.reference;
            this.orderType = value.orderType;
            this.orderId = value.orderId;
            this.status = value.status;
            this.quote = value.quote;
            this.pickup = value.pickup;
            this.dropoff = value.dropoff;
            this.orderItems = value.orderItems;
            this.distance = value.distance;
            this.createdAt = value.createdAt;
            this.updatedAt = value.updatedAt;
            this.pickupAtReady = value.pickupAtReady;
            this.pickupAtEta = value.pickupAtEta;
            this.pickupAt = value.pickupAt;
            this.dropoffAtEta = value.dropoffAtEta;
            this.dropoffAt = value.dropoffAt;
            this.liveDropoffAtEta = value.liveDropoffAtEta;
            this.livePickupAtEta = value.livePickupAtEta;
            this.deliveryTimeUtc = value.deliveryTimeUtc;
            this.fullServiceProvider = value.fullServiceProvider;
            this.fee = value.fee;
            this.tip = value.tip;
            this.currency = value.currency;
            this.tax = value.tax;
            this.total = value.total;
            this.trackingUrl = value.trackingUrl;
            this.courier = value.courier;
            this.driver = value.driver;
            this.refund = value.refund;
            this.requiresId = value.requiresId;
            this.requiresDropoffSignature = value.requiresDropoffSignature;
            this.deliverableAction = value.deliverableAction;
            this.location = value.location;
            this.company = value.company;
            this.serviceProvider = value.serviceProvider;
            this.providers = value.providers;
            this.driverId = value.driverId;
            this.managerNote = value.managerNote;
            this.attributes = value.attributes;
            this.paymentMethods = value.paymentMethods;
            this.warnings = value.warnings;
            this.errorCode = value.errorCode;
            this.loading = value.loading;
            this.selected = value.selected;
        }
    }

    @DtoExport([
        {
            type: DTO_DEFAULT,
            dto: DeliveryOrderSchema,
        },
        {
            type: DTO_CUSTOM,
            dto: DeliveryOrderQuoteRequestSchema,
        },
        {
            type: DTO_DEFAULT_CREATE,
            dto: CreateDeliveryOrderRequestSchema,
        },
    ])
    export(type?: DtoType): any {}

    /**
     * Validates if this item matches the selected groups.
     * @param groupBy
     * @param selectedGroups
     * @returns {boolean}
     */
    isMatchingGroup(
        groupBy: ListGroupBy,
        selectedGroups: string[]
    ): boolean {
        const isMatched: boolean = false;
        return isMatched;
    }

    /**
     * Validates if this item matches the filter.
     * @param filterBy
     * @param filterValue
     * @returns {boolean}
     */
    isMatchingFilter(filterBy: string, filterValue: any): boolean {
        let isMatching: boolean = true;
        return isMatching;
    }

    /**
     * Returns the relevant group data for this item.
     * @param groupBy
     * @returns {string}
     */
    getGroup(groupBy?: ListGroupBy): string {
        let group: string = '';

        return group;
    }

    /**
     * Custom comparision used by sort.
     * Ref is the value to sort / compare by.
     * @param ref
     */
    valueOf(ref?: ListSortBy): any {
        let res: any = this;
        return res;
    }
}
